/** Page-access permissions API (Phase 9 §Part1).
 *
 * Types + react-query hooks for the platform super-admin permission panel and
 * the per-user PermissionsContext. Mirrors the pattern in admin.ts.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// ── Types ─────────────────────────────────────────────────────────────────────────────────────────

export interface PagePermission {
  slug: string;
  label: string;
  category: "default" | "optional";
  enabled: boolean;
  locked: boolean;
}

export interface OrgPermissions {
  org_id: string;
  org_name: string;
  permissions: PagePermission[];
  /** True when the calling user is a platform super-admin. */
  is_super_admin: boolean;
}

export interface OrgSummary {
  id: string;
  name: string;
  user_count: number;
}

// ── Query keys ────────────────────────────────────────────────────────────────────────────────────

const MY_PERMISSIONS_KEY = ["me-permissions"] as const;
const ORGS_KEY = ["admin-orgs"] as const;
const orgPermsKey = (orgId: string) => ["admin-org-permissions", orgId] as const;

// ── Hooks ─────────────────────────────────────────────────────────────────────────────────────────

/** Fetch the calling user's own org's page permissions (+ is_super_admin flag). */
export function useMyPermissions() {
  return useQuery({
    queryKey: MY_PERMISSIONS_KEY,
    queryFn: async (): Promise<OrgPermissions> => {
      const { data } = await api.get<OrgPermissions>("/me/permissions");
      return data;
    },
    // Fail-open on error: a permissions outage should never lock the app.
    // The error is silently absorbed; the context defaults to allow-all.
    retry: 1,
    staleTime: 60 * 1000,
  });
}

/** List all orgs (super-admin only). */
export function useOrgs(enabled = true) {
  return useQuery({
    queryKey: ORGS_KEY,
    queryFn: async (): Promise<OrgSummary[]> => {
      const { data } = await api.get<OrgSummary[]>("/admin/orgs");
      return data;
    },
    enabled,
    staleTime: 30 * 1000,
  });
}

/** Fetch a specific org's page permissions (super-admin only). */
export function useOrgPermissions(orgId: string | null, enabled = true) {
  return useQuery({
    queryKey: orgPermsKey(orgId ?? ""),
    queryFn: async (): Promise<OrgPermissions> => {
      const { data } = await api.get<OrgPermissions>(
        `/admin/orgs/${encodeURIComponent(orgId!)}/permissions`,
      );
      return data;
    },
    enabled: enabled && !!orgId,
    staleTime: 30 * 1000,
  });
}

/** Update a specific org's page permission toggles (super-admin only). */
export function useUpdateOrgPermissions(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (permissions: Record<string, boolean>): Promise<OrgPermissions> => {
      const { data } = await api.put<OrgPermissions>(
        `/admin/orgs/${encodeURIComponent(orgId!)}/permissions`,
        { permissions },
      );
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void qc.invalidateQueries({ queryKey: orgPermsKey(orgId) });
      }
      // Also invalidate my-permissions in case the super-admin is editing their own org.
      void qc.invalidateQueries({ queryKey: MY_PERMISSIONS_KEY });
    },
  });
}
